import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminNpcs } from '../hooks/useAdminNpcs';
import { useAdminTasks } from '../hooks/useAdminTasks';
import { useAdminLevels } from '../hooks/useAdminLevels';
import {
  TaskFormFields,
  EMPTY_TASK_FORM_STATE,
  TaskFormState,
  parseTaskFormContent,
} from '../components/TaskFormFields';

export function AdminCreateTaskPage() {
  const navigate = useNavigate();
  const { npcs, loading: npcsLoading } = useAdminNpcs();
  const { tasks: existingTasks, loading: tasksLoading } = useAdminTasks();
  const { levels, loading: levelsLoading } = useAdminLevels();

  const [form, setForm] = useState<TaskFormState>(EMPTY_TASK_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('Task name is required');
      return;
    }

    const content = parseTaskFormContent(form);
    if (content.parseError) {
      setError(content.parseError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.createLitLegionTask({
        name: form.name.trim(),
        description: form.description.trim(),
        taskType: form.taskType,
        category: form.category.trim() || undefined,
        xpReward: form.xpReward,
        npcId: form.npcId !== 'none' ? form.npcId : undefined,
        parentId: form.parentId !== 'none' ? form.parentId : undefined,
        minLevelId: form.minLevelId !== 'none' ? form.minLevelId : undefined,
        isActive: form.isActive,
        npcTextIntro: form.npcTextIntro.trim() || undefined,
        npcTextObjectives: form.npcTextObjectives.trim() || undefined,
        npcTextInProgress: form.npcTextInProgress.trim() || undefined,
        npcTextSuccess: form.npcTextSuccess.trim() || undefined,
        definition: content.definition,
        privateDefinition: content.privateDefinition,
        coinRewards: form.coinRewards.filter((c) => c.coinId.trim()),
        badgeRewards: form.badgeRewards.filter((b) => b.badgeId.trim()),
      });
      navigate('/a/arche_lit_legion/admin/tasks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin/tasks"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Task List
        </Link>
        <h1 className="text-2xl font-semibold">Create Task</h1>
        <p className="text-sm text-muted-foreground mt-1">Add a new task for players to complete</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <TaskFormFields
          value={form}
          onChange={setForm}
          parentTaskOptions={existingTasks}
          parentTasksLoading={tasksLoading}
          npcs={npcs}
          npcsLoading={npcsLoading}
          levels={levels}
          levelsLoading={levelsLoading}
        />

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/a/arche_lit_legion/admin/tasks')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
